import { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

interface ImageLightboxProps {
  isOpen: boolean;
  onClose: () => void;
  beforeSrc: string;
  afterSrc: string;
  toolName: string;
  beforeAlt?: string;
  afterAlt?: string;
}

export function ImageLightbox({ isOpen, onClose, beforeSrc, afterSrc, toolName, beforeAlt, afterAlt }: ImageLightboxProps) {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-fade-in" />

      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
      >
        <X className="w-6 h-6" />
      </button>

      <div
        className="relative z-10 w-full max-w-5xl animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-center text-white/80 text-sm mb-4 font-medium">{toolName}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div className="relative">
            <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-black/60 text-white text-xs font-medium backdrop-blur-sm">
              Before
            </div>
            <img
              src={beforeSrc}
              alt={beforeAlt || `${toolName} - Before`}
              className="w-full rounded-xl object-cover shadow-2xl"
            />
          </div>

          <div className="relative">
            <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-primary-600/80 text-white text-xs font-medium backdrop-blur-sm">
              After
            </div>
            <img
              src={afterSrc}
              alt={afterAlt || `${toolName} - After`}
              className="w-full rounded-xl object-cover shadow-2xl"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
